import { List } from 'immutable'
import React, { ReactElement } from 'react'

import Employee from '../../../model/employee'
import LeaveType from '../../../model/leaveType'
import Project from '../../../model/project'
import SalaryType from '../../../model/salaryType'
import TimeRegistration from '../../../model/timeRegistration'
import { TimeRegistrationReducer } from '../../../reducers/timeRegistrations'
import { formatDate } from '../../../utils/date-utils'
import { formatLeaveTypeName } from '../../../utils/format-utils'
import { formatDisplayNumber } from '../../../utils/number-utils'
import { getSalaryType } from '../../../utils/salary-type-utils'
import { t } from '../../../utils/translation-utils'
import Icon from '../../elements/icon'
import Table from '../../elements/table'
import Tooltip from '../../elements/tooltip'
import { TimeRegistrationMode } from '../../types'

type Props = {
  employee: Employee
  mode: TimeRegistrationMode
  timeRegistrations: TimeRegistrationReducer
  salaryTypes: List<SalaryType>
  leaveTypes: List<LeaveType>
  projects: List<Project>
}

export default function DetailedTimeRegistrationHistoryTab(props: Props): ReactElement | null {
  type TimeRegistrationRow = {
    key: string
    id: string
    date: string
    description: string
    hours: string
    note?: string
  }
  const columns = [
    { title: t('time_registration_tab.detailed.history.table.header.date'), dataIndex: 'date', key: 'date' },
    {
      title: t('time_registration_tab.detailed.history.table.header.description'),
      dataIndex: 'description',
      key: 'description',
    },
    { title: t('time_registration_tab.detailed.history.table.header.hours'), dataIndex: 'hours', key: 'hours' },
    {
      title: t('time_registration_tab.detailed.history.table.header.note'),
      key: 'x2',
      className: 'employee-table-actions',
      render: (timeRegistration: TimeRegistrationRow) => {
        if (!timeRegistration.note) {
          return null
        }
        return (
          <div>
            <Tooltip title={timeRegistration.note}>
              <span>
                <Icon type="document" />
              </span>
            </Tooltip>
          </div>
        )
      },
    },
  ]

  const matchesModeClass = (timeRegistration: TimeRegistration): boolean => {
    switch (props.mode) {
      case 'Hours':
        return timeRegistration.class === 'Hours'
      case 'FlexTime':
        return timeRegistration.class === 'Flex'
      case 'Overtime':
        return timeRegistration.class === 'Overtime'
      case 'Project':
        return timeRegistration.class === 'Project Hours'
      default:
        return false
    }
  }

  const mapTimeRegistrations = (timeRegistrations: List<TimeRegistration>): TimeRegistrationRow[] => {
    return timeRegistrations
      .filter(
        (timeRegistration) =>
          timeRegistration.employeeID === props.employee.id &&
          matchesModeClass(timeRegistration) &&
          timeRegistration.settled
      )
      .map((timeRegistration) => {
        let title: undefined | string
        let hours = formatDisplayNumber(timeRegistration.hours)
        if (timeRegistration.class === 'Hours') {
          const salary = props.employee.activeContract?.remuneration?.salary.find(
            (row) => row.salaryTypeID === timeRegistration.salaryTypeID
          )
          title = salary?.title
          if (!title) {
            title = getSalaryType(props.salaryTypes.toArray(), timeRegistration.salaryTypeID)?.title || ''
          }
        } else if (timeRegistration.class === 'Flex' || timeRegistration.class === 'Overtime') {
          const num = (timeRegistration.minutes || 0) / 60
          if (props.mode === 'Overtime') {
            title = formatLeaveTypeName('DenmarkOvertime')
          } else {
            title = formatLeaveTypeName('DenmarkFlexTime')
          }
          if ((timeRegistration.minutes || 0) < 0) {
            hours = t('time_registration_tab.detailed.history.table.title.earned', {
              hours: formatDisplayNumber(num * -1),
            })
          } else {
            hours = t('time_registration_tab.detailed.history.table.title.used', { hours: formatDisplayNumber(num) })
          }
        } else {
          title = props.projects.find((p) => p.id === timeRegistration.projectID)?.name || ''
        }
        return {
          key: timeRegistration.id,
          id: timeRegistration.id,
          date: formatDate(timeRegistration.date),
          description: title,
          hours,
          note: timeRegistration.note,
        }
      })
      .toArray()
  }

  const rows = mapTimeRegistrations(props.timeRegistrations.timeRegistrations)

  return (
    <div>
      <Table columns={columns} dataSource={rows} pagination={false} />
    </div>
  )
}
